/**
 * Item page blocks — V98-3 review U1 (no dead ends), U10, U14.
 *
 *   LockedContent   what exactly is signed: the locked version's content
 *                   (`#locked` — the target of every "Read vN" link).
 *   ReviseBlock     a RETURNED request (S3): the approver's reason, the
 *                   change note, and Resubmit → freezes v+1 and re-routes it
 *                   (POST /signatures/:id/resubmit — the author or the
 *                   requester only, server-enforced).
 *   WorkItemBlock   a work item by its server action contract (S10): a
 *                   plain task is marked done here, evidence needs its
 *                   reference, an owning record opens directly.
 *   RecordBlock     findings / recurring obligations: deep link to the record.
 *   StatusBlock     a lead or admin with no hat on the item: who has it, when
 *                   it is due, and what they can do (nudge, reassign, open).
 *   NobodyEligible  add an independent party (owner/admin) or ask an admin.
 */
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, Textarea } from '@aigovops/design-system';
import type { OwedRow } from '@aigovops/core';
import { ApiError } from '../../lib/api';
import { ASSIGNABLE_KINDS, completeWorkItem, getSignature, resubmitSignature, type BlockerRow, type SignatureView } from '../../lib/coverage-api';
import { nudgeMessage, useNudges } from '../../lib/nudge-state';
import { dueChip, fmtDay, itemHref, nameOf, nextActionText, rowAct, type Names } from '../../lib/today-model';
import { AskAdmin } from '../AskAdmin';
import { AssignInline } from '../today/AssignInline';
import { Chip, errorText } from '../today/shared';
import { fmtStamp } from '../today/SignQueue';

type Done = (msg: string, focusId: string) => void;

/** The locked content of a signature request, readable before signing (U10 "Read vN"). */
export function LockedContent({ request }: { request: SignatureView }) {
  const c = (request.content ?? {}) as Record<string, unknown>;
  const lists = (['summary', 'blocks'] as const).flatMap((k) => (Array.isArray(c[k]) ? [[k, c[k] as unknown[]] as const] : []));
  const facts = Object.entries(c).filter(([k, v]) => !['summary', 'blocks'].includes(k) && (typeof v === 'string' || typeof v === 'number'));
  return (
    <section className="gp-hat-block gp-locked" id="locked" aria-labelledby="locked-h" tabIndex={-1}>
      <h2 id="locked-h">{`What’s signed · v${request.version}`}</h2>
      <p className="gp-dim">{`Locked ${fmtStamp(request.frozen_at)}`}</p>
      {lists.map(([k, items]) => (
        <ul key={k} aria-label={k === 'summary' ? 'Summary' : 'Includes'}>{items.map((x, i) => <li key={i}>{String(x)}</li>)}</ul>
      ))}
      {facts.length ? (
        <dl className="gp-dl">{facts.map(([k, v]) => <Fragment key={k}><dt>{k.replace(/_/g, ' ')}</dt><dd>{String(v)}</dd></Fragment>)}</dl>
      ) : null}
      {!lists.length && !facts.length ? <p className="gp-dim">The locked version has no readable summary; open it from its program page.</p> : null}
    </section>
  );
}

/** Revise and resubmit a returned request (S3): reason → change note → v+1, re-routed. */
export function ReviseBlock({ slug, prefix, requestId, names, onDone }: { slug: string; prefix: string; requestId: string; names: Names; onDone: Done }) {
  const qc = useQueryClient();
  const sig = useQuery({ queryKey: ['signature', slug, requestId], queryFn: () => getSignature(slug, requestId), retry: false });
  const [note, setNote] = useState('');
  const [noteErr, setNoteErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<SignatureView | null>(null);
  /** The server answered 409 `unchanged` (the source didn't change after all). */
  const [conflict, setConflict] = useState(false);
  if (sig.isLoading) return <p className="gp-dim">Loading what was sent back…</p>;
  const r = sig.data?.request;
  if (!r) return <Alert variant="error">The request could not load.</Alert>;
  if (done) {
    return (
      <div className="gp-state gp-state--ok" id="revise-state" tabIndex={-1}>
        <strong>{`Resubmitted · v${done.version} · with ${nameOf(names, done.approver_user_id, 'the approver')}`}</strong>
        <span><Link to={`${itemHref(prefix, { kind: 'signature_request', id: done.request_id })}#locked`}>{`Read v${done.version}`}</Link></span>
      </div>
    );
  }
  // R3-S2: the server compares the source with the returned version; nothing changed → Resubmit stays disabled,
  // and says why (the explanation is the button's description, so a screen reader hears it on the button).
  const unchanged = sig.data?.resubmit?.changed === false || conflict;
  if (r.state !== 'returned') {
    return <div className="gp-state" id="revise-state" tabIndex={-1}><strong>{`v${r.version} · ${r.state === 'requested' ? `with ${nameOf(names, r.approver_user_id, 'the approver')}` : r.state}`}</strong></div>;
  }
  const submit = async () => {
    if (busy) return;
    const t = note.trim();
    if (t.length < 10) { setNoteErr(`Say what changed (at least 10 characters). ${t.length}/10.`); return; }
    setBusy(true); setError(null);
    try {
      // R3-S2: a sourced artifact is re-frozen from its current source server-side; only the note travels.
      const out = await resubmitSignature(slug, r.request_id, sig.data?.resubmit?.source === 'artifact'
        ? { change_note: t } : { content: { ...((r.content ?? {}) as object), change_note: t }, change_note: t });
      await Promise.all([qc.invalidateQueries({ queryKey: ['today', slug] }), qc.invalidateQueries({ queryKey: ['signature', slug] }), qc.invalidateQueries({ queryKey: ['blockers', slug] })]);
      setDone(out.request);
      onDone(`Resubmitted ${r.title} as v${out.request.version}`, 'revise-state');
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && /unchanged/i.test(`${e.message} ${JSON.stringify(e.body ?? '')}`)) setConflict(true);
      else setError(errorText(e, 'It could not be resubmitted.'));
    }
    finally { setBusy(false); }
  };
  const editLink = r.artifact_kind === 'policy' ? `${prefix}/policies` : null;
  return (
    <div className="gp-form" id="revise-state" tabIndex={-1}>
      <div className="gp-state gp-state--warn">
        <strong>{`Sent back · v${r.version}${r.decided_at ? ` · ${fmtDay(r.decided_at)}` : ''}`}</strong>
        <span>{r.reason ?? 'The approver asked for changes.'}</span>
        <span><Link to="#locked">{`Read v${r.version}`}</Link>{r.artifact_kind === 'policy' ? <> · <Link to={`${prefix}/policies`}>Edit the policy text</Link></> : null}</span>
      </div>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id="revise-note" label="What changed" required hint={`Goes to the approver with v${r.version + 1} · at least 10 characters`} error={noteErr ?? undefined}>
        <Textarea rows={3} maxLength={2000} value={note} onChange={(e) => { setNote(e.currentTarget.value); setNoteErr(null); setError(null); }} />
      </Field>
      <div className="gp-actions">
        <Button variant="primary" aria-disabled={busy || unchanged || undefined} aria-describedby={unchanged ? 'revise-unchanged' : undefined}
          onClick={() => { if (!unchanged) void submit(); }}>{busy ? 'Resubmitting…' : `Resubmit as v${r.version + 1}`}</Button>
        {unchanged ? (
          <span className="gp-dim" id="revise-unchanged" data-part="resubmit-unchanged">
            {`Nothing differs from v${r.version} yet — make the change first, then resubmit.`}
            {editLink ? <> <Link to={editLink}>Edit the policy</Link></> : null}
          </span>
        ) : null}
      </div>
      <LockedContent request={r} />
    </div>
  );
}

/** A work item through its server action contract (S10). */
export function WorkItemBlock({ slug, prefix, row, names, onDone }: { slug: string; prefix: string; row: OwedRow; names: Names; onDone: Done }) {
  const qc = useQueryClient();
  const [ref, setRef] = useState('');
  const [refErr, setRefErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const a = row.act;
  const resubmit = a?.kind === 'endpoint' && a.path ? /^\/signatures\/([^/]+)\/resubmit$/.exec(a.path) : null;
  if (resubmit) return <ReviseBlock slug={slug} prefix={prefix} requestId={decodeURIComponent(resubmit[1]!)} names={names} onDone={onDone} />;
  if (done) return <div className="gp-state gp-state--ok" id="work-state" tabIndex={-1}><strong>Done</strong></div>;
  const evidence = row.subtype === 'evidence';
  const complete = async () => {
    if (busy) return;
    if (evidence && !ref.trim()) { setRefErr('Add the evidence link or ID first.'); return; }
    setBusy(true); setError(null);
    try {
      await completeWorkItem(slug, row.ref.id, evidence ? { completion_ref: ref.trim() } : {});
      await qc.invalidateQueries({ queryKey: ['today', slug] });
      setDone(true);
      onDone(`Done · ${row.title}`, 'work-state');
    } catch (e) { setError(errorText(e, 'It could not be marked done.')); }
    finally { setBusy(false); }
  };
  if (a && (row.subtype === 'task' || evidence) && a.kind === 'endpoint') {
    return (
      <div className="gp-form" id="work-state" tabIndex={-1}>
        {error ? <Alert variant="error">{error}</Alert> : null}
        {evidence ? (
          <>
            <Field id="work-evidence" label="Evidence link or ID" required hint="Upload it in Evidence first if it isn’t there yet" error={refErr ?? undefined}>
              <Input value={ref} maxLength={500} onChange={(e) => { setRef(e.currentTarget.value); setRefErr(null); setError(null); }} />
            </Field>
            <p className="gp-dim"><Link to={`${prefix}/evidence`}>Open Evidence</Link></p>
          </>
        ) : null}
        <div className="gp-actions"><Button variant="primary" aria-disabled={busy || undefined} onClick={() => void complete()}>{busy ? 'Saving…' : a.label}</Button></div>
      </div>
    );
  }
  const act = rowAct(row, prefix);
  return (
    <div className="gp-actions" id="work-state" tabIndex={-1}>
      {act.kind === 'link' ? <Link className="gp-linkbtn btn-primary" to={act.to}>{act.label}</Link> : null}
    </div>
  );
}

/**
 * Findings and recurring obligations: open the record where it is worked.
 * A finding is fixed in its program's Operate queue (remediate / reassign /
 * deadline / close live there, not on /findings), deep-linked to its row.
 * With no program known, it opens the finding itself.
 */
export function RecordBlock({ kind, id, prefix, program }: { kind: string; id: string; prefix: string; program?: string | null }) {
  if (kind === 'finding') {
    const to = program ? `${prefix}/programs/${program}/operate?finding=${encodeURIComponent(id)}` : `${prefix}/findings?finding=${encodeURIComponent(id)}`;
    return <div className="gp-actions"><Link className="gp-linkbtn btn-primary" to={to}>{program ? 'Fix in Operate' : 'Open finding'}</Link></div>;
  }
  return <div className="gp-actions"><Link className="gp-linkbtn btn-primary" to={`${prefix}/deliverables?occurrence=${encodeURIComponent(id)}`}>Complete in Calendar</Link></div>;
}

/** Nobody can act: an owner/admin adds an independent party; anyone else asks one. */
export function NobodyEligible({ slug, title, onAddParty }: { slug: string; title: string; onAddParty?: (opener: HTMLElement) => void }) {
  return (
    <div className="gp-form">
      <div className="gp-state gp-state--warn"><strong>Nobody eligible to act yet</strong></div>
      <div className="gp-actions">
        {onAddParty
          ? <Button id="nobody-add-party" onClick={(e) => onAddParty(e.currentTarget)}>Add an independent party</Button>
          : <AskAdmin slug={slug} compact verb="add an independent party" what="adds independent parties" subject={`GAC Pro: nobody is eligible to act on ${title}`} />}
      </div>
    </div>
  );
}

/** Read-only status for a lead / admin with no hat on the item (U1): who has it, and what the lead can do. */
export function StatusBlock({ slug, prefix, row, names, canAssign, onDone, todayDay, program }: {
  slug: string; prefix: string; row: BlockerRow; names: Names; canAssign: boolean; onDone: Done; todayDay: string;
  /** Program key whose Operate queue fixes a finding (RecordBlock). */
  program?: string | null;
}) {
  const nudges = useNudges(slug);
  const [open, setOpen] = useState(false);
  const chip = dueChip(row, todayDay);
  const holder = row.holder_ids[0] ?? null;
  const holderName = row.holders[0] ?? 'Unassigned';
  const s = nudges.state[row.item_key];
  const subject = row.ref && ASSIGNABLE_KINDS[row.ref.kind] ? { kind: ASSIGNABLE_KINDS[row.ref.kind]!, id: row.ref.id } : null;
  const nudge = async () => {
    const r = await nudges.send(row.item_key, { user_id: holder, name: holderName });
    onDone(nudgeMessage(r, holderName), 'status-nudge');
  };
  return (
    <section className="gp-hat-block" aria-labelledby="hat-status">
      <h2 id="hat-status">Status</h2>
      <div className="gp-state" id="status-state" tabIndex={-1}>
        <strong>{row.holders.length ? `With ${row.holders.join(', ')}` : 'Nobody owns it yet'}</strong>
        <span className="gp-ow-meta"><Chip text={chip.text} tone={chip.tone} /><span>{nextActionText(row.next_action)}</span></span>
      </div>
      <div className="gp-actions">
        {holder ? (s ? <Chip id="status-nudge" text={s.kind === 'sent' ? `Nudged · ${s.to}` : s.kind === 'already' ? 'Nudged today' : s.kind === 'away' ? `Away until ${fmtDay(s.until)}` : s.text} tone="lock" />
          : row.last_nudged_at?.slice(0, 10) === todayDay ? <Chip id="status-nudge" text="Nudged today" tone="lock" />
          : <Button id="status-nudge" onClick={() => void nudge()} aria-label={`Nudge ${holderName} · ${row.title}`}>{`Nudge ${holderName.split(' ')[0]}`}</Button>) : null}
        {canAssign && subject ? <Button id="status-assign" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{holder ? 'Reassign' : 'Assign'}</Button> : null}
        {row.ref && (row.ref.kind === 'finding' || row.ref.kind === 'obligation_occurrence') ? <RecordBlock kind={row.ref.kind} id={row.ref.id} prefix={prefix} program={program} /> : null}
      </div>
      {open && subject ? (
        <AssignInline slug={slug} subject={subject} title={row.title} responsibility={row.hat === 'preparer' ? 'owner' : row.hat} fromUserId={holder} names={names}
          openerId="status-assign" onClose={() => setOpen(false)} onDone={(m) => { setOpen(false); onDone(m, 'status-state'); }} />
      ) : null}
    </section>
  );
}
